import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useState } from "react";
import { FormStatus, ICampaign, ICampaignBeneficiaryDetail } from "./Addressable.types";
import dayjs from "dayjs";
import { Chip, IconButton, Menu, MenuItem } from "@mui/material";
import { ptBR } from "@mui/x-data-grid/locales";
import { cpfMask } from "utils/cpf";
import { formatCNPJ } from "utils/is-valid-cnpj";
import { MoreVert } from "@mui/icons-material";
import { useAlert } from "contexts/alert";

type Props = {
  campaign: ICampaign;
}

export function AddressableCampaignBeneficiaries({ campaign }: Props) {
  const { openAlert } = useAlert();
  const [selectedRowUrl, setSelectedRowUrl] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, campaignUrl: string | null) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowUrl(campaignUrl);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowUrl(null);
  };

  const handleCopyUrl = async () => {
    if (selectedRowUrl) {
      try {
        await navigator.clipboard.writeText(selectedRowUrl);
        openAlert("URL copiada com sucesso!", "success");
      } catch (error) {
        console.error("Erro ao copiar URL:", error);
        openAlert("Erro ao copiar URL", "error");
      }
    }
    handleMenuClose();
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "-";
    return dayjs(date).format("DD/MM/YYYY HH:mm");
  };

  const formatStatus = (status: FormStatus) => {
    const statusConfig = {
      PENDING: {
        label: "Em andamento",
        backgroundColor: "rgba(2, 136, 209, 0.08)",
        color: "#01579B"
      },
      FINISHED: {
        label: "Concluída",
        backgroundColor: "rgba(46, 125, 50, 0.08)",
        color: "#2E7D32"
      },
      FAILED: {
        label: "Falha",
        backgroundColor: "rgba(125, 46, 46, 0.08)",
        color: "#7d2e2e"
      },
    };

    const config = statusConfig[status];

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.backgroundColor,
          color: config.color,
          fontWeight: 500,
        }}
      />
    );
  };

  const formatPersonType = (type: "employee" | "dependent") => {
    return type === "employee" ? "Titular" : type === "dependent" ? "Dependente" : '-';
  };

  const columns: GridColDef<ICampaignBeneficiaryDetail>[] = [
    {
      field: "name",
      headerName: "Nome completo",
      width: 300,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "taxPayerId",
      headerName: "CPF do Beneficiário",
      flex: 1,
      renderCell: (params) => !params.value ? '-' : cpfMask(params.value),
    },
    {
      field: "taxPayerIdEmployee",
      headerName: "CPF do Titular",
      flex: 1,
      renderCell: (params) => !params.value ? '-' : cpfMask(params.value),
    },
    {
      field: "legalEntityTaxId",
      headerName: "CNPJ do PJ",
      flex: 1,
      renderCell: (params) => !params.value ? '-' : formatCNPJ(params.value, false),
    },
    {
      field: "type",
      headerName: "Tipo do beneficiário",
      flex: 1,
      renderCell: (params) => formatPersonType(params.value),
    },
    {
      field: "finishedDate",
      headerName: "Data de resposta",
      flex: 1,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => formatStatus(params.value),
    },
    {
      field: "actions",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          disabled={params.row.type === "dependent"}
          onClick={(event) => handleMenuOpen(event, params.row.campaignUrl)}
          sx={{
            opacity: params.row.type === "dependent" ? 0.3 : 1,
            cursor: params.row.type === "dependent" ? "not-allowed" : "pointer",
          }}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  const rows = campaign.beneficiaries.map((beneficiary) => ({
    id: beneficiary.taxPayerId,
    ...beneficiary,
  }));

  return (
    <>
      <div style={{ width: "100%", display: "flex", flexDirection: "row", gap: "16px", marginBottom: "30px" }}>
        {campaign.metrics.map((metric, index) => (
          <div key={index} style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: 0 }}>{metric.label}</p>
            <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{metric.value}</span>
          </div>
        ))}
      </div>
      <DataGrid
        rows={rows}
        columns={columns}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
            },
          },
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        style={{ backgroundColor: "white", minHeight: 400 }}
        disableColumnFilter={false}
        disableColumnSelector={false}
        disableDensitySelector={false}
        scrollbarSize={10}
        sx={{
          "& .MuiDataGrid-row[data-rowselectable='false']": {
            "& .MuiCheckbox-root": {
              opacity: 0.3,
              cursor: "not-allowed",
            },
            "& .MuiCheckbox-root.Mui-disabled": {
              opacity: 0.3,
              cursor: "not-allowed",
            },
          },
          "& .MuiDataGrid-checkboxInput.Mui-disabled": {
            opacity: 0.3,
            cursor: "not-allowed",
          },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={handleCopyUrl}
          disabled={!selectedRowUrl}
        >
          Copiar URL Formulário
        </MenuItem>
      </Menu>
    </>
  );
}
